// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';

import {ModalEl, ModalType} from 'common/components/modal';
import {TrackedUserIconEl} from 'common/widgets/tracked-user-icon';
import {renameEvent} from 'common/report/util/events';
import {ExpandedRowEl} from 'common/report/events/expanded-row/index';
import {PropertyKeyFilter} from 'common/report/events/types';
import {SpinnerEl} from 'common/components/spinner';
import {SpinnerSize} from 'common/components/spinner/types';
import {InputEl} from 'common/components/input';
import {InputSize} from 'common/components/input/types';
import {SvgIconEl} from 'common/components/svg-icon';
import {ServerErrorHero} from 'common/widgets/warning-hero/presets';
import {renderCellProperty} from 'common/report/events/table-cell-mixins';
import {ButtonSize, ButtonTheme} from 'common/components/button/constants';
import {ButtonEl} from 'common/components/button';
import {InspectorEvent} from 'common/report/queries/query-api/inspector';
import {IconSizeType} from 'common/constants';

import {WarningHeroReportName} from '../warning-hero/constants';
import {NoDataHeroPanel} from '../warning-hero/no-data-hero';
import type {EventsSidebarWidget} from './index';

export function template(this: EventsSidebarWidget) {
  const handleSelect = (idx: number) => {
    this.helpers.handleSelect(idx);
  };
  const closeSidebar = () => {
    this.dispatchEvent(new CustomEvent(`close`));
  };

  let contents;

  if (this.state.selectedEvent) {
    const event = this.state.selectedEvent;
    contents = (
      <>
        {sidebarHeaderDetails(this.state.url, this.helpers.handleUnselect.bind(this), closeSidebar)}
        <div sel='.events-sidebar-content-container'>
          <div sel='.events-sidebar-event-expanded'>
            <div sel='.events-sidebar-event-expanded-header'>
              <ButtonEl
                sel='.events-sidebar-event-page-control'
                on={{click: () => this.helpers.handleSelect(this.state.selectedRowIdx - 1)}}
                class={{'events-sidebar-event-page-control-disabled': this.state.selectedRowIdx === 0}}
                attrs={{
                  theme: ButtonTheme.Tertiary,
                  title: `Previous event (viewing ${this.state.selectedRowIdx + 1} of ${this.state.events.length})`,
                }}
              >
                <SvgIconEl attrs={{icon: `arrow-left`}} />
              </ButtonEl>
              <div sel='.events-sidebar-event-details'>
                <div sel='.events-sidebar-event-detail-name'>
                  <div sel='.events-sidebar-event-detail-name-label'>{renameEvent(event.event)} </div>
                </div>
                <div sel='.events-sidebar-event-detail-subproperty'>
                  {renderCellProperty(event, `time`, this.getContext(`i18n`))}
                </div>
              </div>
              <ButtonEl
                sel='.events-sidebar-event-page-control'
                on={{click: () => this.helpers.handleSelect(this.state.selectedRowIdx + 1)}}
                class={{
                  'events-sidebar-event-page-control-disabled':
                    this.state.selectedRowIdx + 1 >= this.state.events.length,
                }}
                attrs={{
                  theme: ButtonTheme.Tertiary,
                  title: `Next event (viewing ${this.state.selectedRowIdx + 1} of ${this.state.events.length})`,
                }}
              >
                <SvgIconEl attrs={{icon: `arrow-right`}} />
              </ButtonEl>
            </div>
            <ExpandedRowEl
              props={{
                pinnedProperties: this.state.pinnedProperties,
                row: event,
              }}
              attrs={{
                toggletheme: `tab-bar`,
                'allow-pinning': true,
                'view-format': this.state.viewFormat,
                'property-key-filter': this.state.propertyKeyFilter,
                toggleoptions: [
                  {
                    value: PropertyKeyFilter.AllProperties,
                    label: `All Properties`,
                  },
                  {
                    value: PropertyKeyFilter.YourProperties,
                    label: `Project`,
                  },
                  {
                    value: PropertyKeyFilter.MixpanelProperties,
                    label: `Mixpanel`,
                  },
                ],
              }}
              on={{
                propertyKeyFilterChange: (ev: CustomEvent<{propertyKeyFilter: PropertyKeyFilter}>) => {
                  this.update({
                    propertyKeyFilter: ev.detail.propertyKeyFilter,
                  });
                },
                pinsupdated: (ev: CustomEvent) => {
                  const {pins} = ev.detail;
                  this.update({
                    pinnedProperties: pins,
                  });
                },
              }}
            />
          </div>
        </div>
      </>
    );
  } else if (this.state.loading) {
    contents = (
      <>
        {sidebarHeader(this.state.url, closeSidebar)}
        <div sel='.events-sidebar-content-container'>
          <InputEl
            sel='.events-sidebar-search-input'
            attrs={{
              icon: `search`,
              placeholder: `Search…`,
              size: InputSize.Medium,
              'show-clear': true,
            }}
            props={{
              maxLength: 50,
              value: this.state.search,
            }}
          />
          <div sel='.events-sidebar-loading'>
            <SpinnerEl attrs={{size: SpinnerSize.Large}} />
          </div>
        </div>
      </>
    );
  } else if (this.state.error) {
    contents = <ServerErrorHero reportName={WarningHeroReportName.EventsSidebar} />;
  } else if (this.state.events.length === 0) {
    contents = <NoDataHeroPanel reportName={WarningHeroReportName.EventsSidebar} />;
  } else {
    contents = (
      <>
        {sidebarHeader(this.state.url, closeSidebar)}
        <div sel='.events-sidebar-content-container.events-sidebar-content-list'>
          <InputEl
            sel='.events-sidebar-search-input'
            attrs={{
              icon: `search`,
              placeholder: `Search…`,
              size: InputSize.Medium,
              'show-clear': true,
            }}
            on={{
              clear: () => this.helpers.onSearchChange(``),
              input: (ev) => this.helpers.onSearchChange(ev.target.value),
            }}
            props={{
              maxLength: 50,
              value: this.state.search,
            }}
          />
          <ul sel='.events-sidebar-events'>
            {this.state.events.map((event: InspectorEvent, idx: number) => {
              return (
                <li
                  sel='.events-sidebar-event'
                  on={{
                    click() {
                      handleSelect(idx);
                    },
                  }}
                >
                  <div sel='.events-sidebar-icon-wrapper'>
                    <TrackedUserIconEl
                      attrs={{
                        'distinct-id': event.properties.distinct_id,
                        email: event.properties.email,
                        size: IconSizeType.Small,
                      }}
                    />
                  </div>
                  <div sel='.events-sidebar-event-detail'>
                    <div sel='.events-sidebar-event-detail-name'>{renameEvent(event.event)}</div>
                    <div sel='.events-sidebar-event-detail-subproperty'>
                      {renderCellProperty(event, `time`, this.getContext(`i18n`))}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </>
    );
  }

  return (
    <ModalEl
      attrs={{
        'modal-type': ModalType.RightDrawer,
        open: true,
        closeable: true,
        'hide-backdrop': true,
      }}
    >
      <div sel='.events-sidebar-wrapper'>{contents}</div>
    </ModalEl>
  );
}

function sidebarHeader(fullPageUrl: string, closeSidebar: () => void) {
  return (
    <div sel='.events-sidebar-header'>
      <h2>Events</h2>
      <ButtonEl
        attrs={{theme: ButtonTheme.Tertiary, size: ButtonSize.Small, href: fullPageUrl}}
        on={{click: closeSidebar}}
      >
        Open as page
      </ButtonEl>
    </div>
  );
}

function sidebarHeaderDetails(fullPageUrl: string, unselect: () => void, closeSidebar: () => void) {
  return (
    <div sel='.events-sidebar-header'>
      <ButtonEl attrs={{theme: ButtonTheme.Tertiary, size: ButtonSize.Small}} on={{click: unselect}}>
        ←
      </ButtonEl>
      <h2>Event Detail</h2>
      <ButtonEl
        attrs={{theme: ButtonTheme.Tertiary, size: ButtonSize.Small, href: fullPageUrl}}
        on={{click: closeSidebar}}
      >
        Open as page
      </ButtonEl>
    </div>
  );
}
